import { useState, useEffect } from 'react';
import { logger } from '../utils/logger';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Global (module-level) storage for deferred prompt
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

export function usePWAInstall() {
  // Na starcie sprawdź, czy globalny handler w main.tsx już przechwycił event
  const globalDeferred = (typeof window !== 'undefined' && (window as any).__deferredInstallPrompt) as BeforeInstallPromptEvent | null
  deferredInstallPrompt = deferredInstallPrompt || globalDeferred || null

  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(deferredInstallPrompt);
  const [isInstallable, setIsInstallable] = useState(!!deferredInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Sprawdź czy aplikacja jest już zainstalowana
    if (window.matchMedia('(display-mode: standalone)').matches || 
        // Sprawdzenie dla iOS Safari (tylko tam istnieje navigator.standalone)
        (('standalone' in window.navigator) && (window.navigator as any).standalone === true)) {
      setIsInstalled(true);
      return;
    }

    // Nasłuchuj zdarzenia beforeinstallprompt (fallback gdyby globalny nasłuch nie zadziałał)
    const handleBeforeInstallPrompt = (e: Event) => {
      // Zapobiegaj automatycznemu wyświetlaniu mini-infobar na mobilnych urządzeniach
      e.preventDefault();
      // Zapisz zdarzenie globalnie i w stanie hooka
      deferredInstallPrompt = e as BeforeInstallPromptEvent;
      setInstallPrompt(deferredInstallPrompt);
      setIsInstallable(true);
      
      logger.debug('📱 PWA jest gotowa do instalacji!');
    };

    // Nasłuchuj zdarzenia appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
      deferredInstallPrompt = null;
      logger.debug('📱 PWA została zainstalowana!');
    };

    // Dodatkowo nasłuchuj custom eventów z globalnego handlera w main.tsx
    const handleGlobalBeforeInstall = () => {
      const ev = (window as any).__deferredInstallPrompt as BeforeInstallPromptEvent | null
      if (ev) {
        deferredInstallPrompt = ev
        setInstallPrompt(ev)
        setIsInstallable(true)
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa:beforeinstallprompt', handleGlobalBeforeInstall)
    window.addEventListener('pwa:appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa:beforeinstallprompt', handleGlobalBeforeInstall)
      window.removeEventListener('pwa:appinstalled', handleAppInstalled)
    };
  }, []);

  // Funkcja do wyświetlania monitu instalacji
  const promptInstall = async () => {
    const prompt = deferredInstallPrompt || installPrompt;
    if (!prompt) {
      logger.debug('📱 Brak zdarzenia instalacji');
      return;
    }

    // Pokaż monit instalacji
    await prompt.prompt();

    // Czekaj na wybór użytkownika
    const choiceResult = await prompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      logger.debug('📱 Użytkownik zaakceptował instalację');
    } else {
      logger.debug('📱 Użytkownik odrzucił instalację');
    }

    // Wyczyść zapisane zdarzenie - można go użyć tylko raz
    setInstallPrompt(null);
    setIsInstallable(false);
    deferredInstallPrompt = null;
  };

  return {
    isInstallable,
    isInstalled,
    promptInstall
  };
}